import React, { useState } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  Button,
  IconButton,
  Box,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { behaviourService, BehaviourType } from 'src/services/behaviourService';
import { useAuth } from 'src/context/auth/AuthContext';
import { useDebounce } from 'src/hooks/useDebounce';
import CreatedBy from 'src/components/CreatedBy';
import moment from 'moment';

export const BehaviourTypes: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getBaseRoute, hasPermission } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'highlight' | 'challenge'>('ALL');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<BehaviourType | null>(null);

  const debouncedSearch = useDebounce(searchInput, 300);

  const {
    data: behaviourTypes = [],
    isLoading,
  } = useQuery({
    queryKey: ['behaviourTypes'],
    queryFn: () => behaviourService.getAllBehaviourTypes(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => behaviourService.deleteBehaviourType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['behaviourTypes'] });
      queryClient.invalidateQueries({ queryKey: ['streetCredMetrics'] });
      toast.success('Behavior type deleted successfully');
      setDeleteModalOpen(false);
      setSelectedType(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete behavior type');
    },
  });

  const filteredList = behaviourTypes.filter((row: BehaviourType) => {
    const matchesSearch =
      row.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (row.description && row.description.toLowerCase().includes(debouncedSearch.toLowerCase()));
    const matchesType = typeFilter === 'ALL' || row.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleOpenDelete = (type: BehaviourType) => {
    setSelectedType(type);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedType?.id) {
      deleteMutation.mutate(selectedType.id);
    }
  };

  const canManageTypes = hasPermission('street_cred.manage');

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" sx={{ flexGrow: 1 }}>
          <TextField
            size="small"
            placeholder="Search behavior titles or descriptions..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="solar:magnifer-linear" width={18} color="#64748B" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <Icon icon="solar:close-circle-bold" width={15} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                height: 38,
                fontSize: '0.875rem',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                width: { xs: '100%', sm: 300, md: 340 },
              },
            }}
          />

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label="All Catalog"
              size="small"
              clickable
              onClick={() => setTypeFilter('ALL')}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'ALL' ? 'primary.main' : 'background.paper',
                color: typeFilter === 'ALL' ? 'primary.contrastText' : 'text.secondary',
                border: '1px solid',
                borderColor: typeFilter === 'ALL' ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'ALL' ? 'primary.dark' : 'action.hover' },
              }}
            />
            <Chip
              icon={<Icon icon="solar:medal-ribbon-star-bold-duotone" width={16} color={typeFilter === 'highlight' ? '#FFFFFF' : '#059669'} />}
              label="Highlights (+)"
              size="small"
              clickable
              onClick={() => setTypeFilter('highlight')}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'highlight' ? '#059669' : 'background.paper',
                color: typeFilter === 'highlight' ? '#FFFFFF' : '#059669',
                border: '1px solid',
                borderColor: typeFilter === 'highlight' ? '#059669' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'highlight' ? '#047857' : 'rgba(5, 150, 105, 0.08)' },
              }}
            />
            <Chip
              icon={<Icon icon="solar:shield-warning-bold-duotone" width={16} color={typeFilter === 'challenge' ? '#FFFFFF' : '#DC2626'} />}
              label="Challenges (-)"
              size="small"
              clickable
              onClick={() => setTypeFilter('challenge')}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: 1.5,
                bgcolor: typeFilter === 'challenge' ? '#DC2626' : 'background.paper',
                color: typeFilter === 'challenge' ? '#FFFFFF' : '#DC2626',
                border: '1px solid',
                borderColor: typeFilter === 'challenge' ? '#DC2626' : 'divider',
                '&:hover': { bgcolor: typeFilter === 'challenge' ? '#B91C1C' : 'rgba(220, 38, 38, 0.08)' },
              }}
            />
          </Stack>
        </Stack>

        {/* Action Button */}
        {canManageTypes && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
            onClick={() => navigate(getBaseRoute('/add-behavior-type'))}
            sx={{
              height: 38,
              borderRadius: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2.25,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
            }}
          >
            + New Behavior Type
          </Button>
        )}
      </Stack>

      {/* Behavior Types Table with comfortable row sizing */}
      <TableContainer>
        <Table>
          <thead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Behavior Name
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Category
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Default Points
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Description / Guidelines
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Created By
              </TableCell>
              <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Created At
              </TableCell>
              <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', textTransform: 'uppercase' }}>
                Actions
              </TableCell>
            </TableRow>
          </thead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.8125rem' }}>
                    Loading behavior types catalog...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredList.length > 0 ? (
              filteredList.map((row: BehaviourType) => {
                const isHighlight = row.type === 'highlight';
                const points =
                  row.points !== 0 && row.points !== null && row.points !== undefined
                    ? Number(row.points)
                    : isHighlight
                    ? 10
                    : -10;

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    {/* Title with distinct Duotone Icon */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            color: isHighlight ? '#059669' : '#DC2626',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            icon={isHighlight ? 'solar:medal-ribbon-star-bold-duotone' : 'solar:danger-triangle-bold-duotone'}
                            width={19}
                          />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                          {row.title}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Category Chip */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={
                          <Icon
                            icon={isHighlight ? 'solar:crown-star-bold-duotone' : 'solar:shield-warning-bold-duotone'}
                            width={14}
                            color={isHighlight ? '#059669' : '#DC2626'}
                          />
                        }
                        label={isHighlight ? 'Positive Highlight' : 'Demerit Challenge'}
                        size="small"
                        sx={{
                          height: 25,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: 1.25,
                          bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: isHighlight ? '#059669' : '#DC2626',
                          border: '1px solid',
                          borderColor: isHighlight ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                        }}
                      />
                    </TableCell>

                    {/* Points Value */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={
                          <Icon
                            icon={isHighlight || points >= 0 ? 'solar:medal-star-bold-duotone' : 'solar:danger-triangle-bold-duotone'}
                            width={13}
                            color={isHighlight || points >= 0 ? '#059669' : '#DC2626'}
                          />
                        }
                        label={`${points >= 0 ? '+' : ''}${points} pts`}
                        size="small"
                        sx={{
                          height: 25,
                          fontSize: '0.8125rem',
                          fontWeight: 700,
                          borderRadius: 1.25,
                          bgcolor: isHighlight || points >= 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                          color: isHighlight || points >= 0 ? '#059669' : '#DC2626',
                          border: '1px solid',
                          borderColor: isHighlight || points >= 0 ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                        }}
                      />
                    </TableCell>

                    {/* Description */}
                    <TableCell sx={{ py: 2, maxWidth: 360 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {row.description || '--'}
                      </Typography>
                    </TableCell>

                    {/* Created By */}
                    <TableCell sx={{ py: 2 }}>
                      <CreatedBy creator={row.creator} />
                    </TableCell>

                    {/* Created Date */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8125rem' }}>
                        {moment(row.createdat).format('MMM DD, YYYY')}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        <Tooltip title="Edit Behavior Type">
                          <IconButton
                            size="small"
                            onClick={() => navigate(getBaseRoute(`/edit-behaviour-type/${row.id}`))}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
                          >
                            <Icon icon="solar:pen-2-bold" width={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Type">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDelete(row)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'rgba(239, 68, 68, 0.08)' } }}
                          >
                            <Icon icon="solar:trash-bin-trash-bold" width={18} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                      color: 'text.disabled',
                    }}
                  >
                    <Icon icon="solar:library-bold-duotone" width={26} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                    No behavior types found
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.5, display: 'block' }}>
                    {searchInput || typeFilter !== 'ALL' ? 'Try adjusting your search query or filters' : 'Define your first school behavior rule in the catalog'}
                  </Typography>
                  {canManageTypes && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                      onClick={() => navigate(getBaseRoute('/add-behavior-type'))}
                      sx={{ mt: 2, borderRadius: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' }}
                    >
                      + Add New Behavior Type
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'rgba(239, 68, 68, 0.12)',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon="solar:trash-bin-trash-bold" width={18} />
          </Box>
          Delete Behavior Type
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Are you sure you want to remove "{selectedType?.title}"? Existing student records will preserve their historical logged points.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteModalOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={deleteMutation.isPending}
            onClick={handleConfirmDelete}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default BehaviourTypes;
